import { useRef, useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import { Flex, isNullish, TextArea, useEventListener } from "@theorgchartdev/storybook";
import { RulePairInput } from "../RulePairInput/RulePairInput";
import { QRulePair } from "@coreapp/CoreApp/QRulePair";
import { appstr } from "@coreapp/utils/appstr";

/**
 * type Props = {
 *   editing: boolean;
 *   rule: QRule;
 *   setRule: Dispatch<SetStateAction<QRule>>
 * }
 */
export const ComplexRuleInput = props => {
  const [error, setError] = useState(null);
  const [rulePairValue, setRulePairValue] = useState(() => {
    const newPair = new QRulePair();
    newPair.InitRulePair("", "", null, "", "");
    return newPair;
  });

  const inputRef = useRef(null);

  const ruleRef = useRef();
  ruleRef.current = props.rule;

  const validateRule = ruleObj => {
    const validationResult = ruleObj.ValidateRule();
    const isValid = validationResult === "success";
    if (isValid) {
      setError(null);
    } else {
      setError(validationResult);
    }
  };

  const handleComplexDefUpdate = e => {
    const rule = ruleRef.current.Clone();
    rule.SetComplexDef(e.target.value || null);
    props.setRule(rule);
    validateRule(rule);
  };

  const handleRulePairUpdate = newRulePair => {
    setRulePairValue(newRulePair);

    const field = newRulePair.GetFieldName();
    const value = newRulePair.GetValue();
    let complexDef = ruleRef.current.GetComplexDef() || "";
    if (value) {
      complexDef = `${complexDef}${value}`;
      const blankRulePair = new QRulePair();
      blankRulePair.InitRulePair("", "", null, "", "");
      setRulePairValue(blankRulePair);
    } else if (field) {
      complexDef = `${complexDef}[${field}]`;
    }

    const rule = ruleRef.current.Clone();
    rule.SetComplexDef(complexDef);
    props.setRule(rule);
    validateRule(rule);
  };

  const handleInputSelection = event => {
    const selection = event.target.value.substring(
      event.target.selectionStart,
      event.target.selectionEnd
    );

    if (selection.charAt(0) !== "[" || selection.charAt(selection.length - 1) !== "]") {
      return;
    }

    const fieldName = selection.substring(1, selection.length - 1);

    const doc = StateLib.GetDoc();
    const fieldId = doc.GetFieldID(fieldName);

    if (!fieldId) {
      return;
    }

    const rulePair = new QRulePair();
    rulePair.InitRulePair(fieldName, fieldId, null, "", "");
    setRulePairValue(rulePair);
  };

  useEventListener("select", handleInputSelection, { elementRef: inputRef });

  const externalError = props.errorsObject?.generalRuleError?.[0];
  const hasError = !isNullish(error) || !!externalError?.hasError;
  const errorMsg = error || (externalError?.hasError ? externalError.errorString : null);

  return (
    <Flex direction="column" gap="16px">
      <TextArea
        inputOnly={false}
        ref={inputRef}
        readOnly={!props.editing}
        onChange={handleComplexDefUpdate}
        value={props.rule?.GetComplexDef() || ""}
        label={appstr("Rule definition")}
        placeholder={appstr("Enter rule definition")}
        dataTestId={props?.dataTestIdPrefix ? `${props.dataTestIdPrefix}-complex-input` : undefined}
        status={hasError ? "error" : undefined}
        message={errorMsg}
        require={props.require}
      />
      {props.editing ? (
        <RulePairInput
          hideComparisonInput
          editing
          value={rulePairValue}
          onChange={handleRulePairUpdate}
          selectSize="medium"
        />
      ) : null}
    </Flex>
  );
};
